/**
 * Snapshot canónico do itinerário partilhado por TODOS os consumidores:
 * agenda visual, mapa, navegação, PDF e ICS.
 *
 * Regras vinculativas:
 * - cada entrada tem um `scheduleEntryId` estável dentro da revisão;
 * - `startAt`/`endAt` vêm sempre da mesma agenda — nunca de horários por índice;
 * - mapa, navegação, PDF e ICS derivam desta lista, nunca de arrays antigos;
 * - uma resposta assíncrona de uma revisão anterior nunca substitui a atual;
 * - módulo puro: sem IO, sem Maps/Places/Routes, sem `Math.random`, sem relógio.
 */
import type { IcsEvent } from "./ics";
import type { DayWindow } from "./day-windows";
import { LOGISTICS_MEALS } from "./schedule";
import { buildRouteDays, isPublishableEntry, type RouteDay } from "./route-days";
import {
  buildRouteSnapshot,
  type RouteBudget,
  type RouteSnapshot,
  type TravelLeg,
} from "./route-snapshot";

export type SnapshotVenue = {
  name: string;
  subtitle?: string;
  blurb?: string;
  priceLabel?: string;
  mapsUrl?: string;
  website?: string;
  phone?: string;
  lat?: number;
  lng?: number;
  placeId?: string;
};

export type SnapshotEntryInput =
  | {
      kind: "stop";
      /**
       * Visita obrigatória (fixa, nomeada ou pedida). Nunca desaparece em
       * silêncio: sem horas válidas a publicação FALHA com motivo.
       */
      required?: boolean;
      /**
       * Identidade lógica ESTÁVEL criada uma única vez quando a paragem foi
       * aceite para esta geração. Não depende do dia, hora, índice ou revisão.
       */
      logicalId?: string;
      day: number;
      name: string;
      startAt: Date;
      endAt: Date;
      blurb?: string;
      note?: string;
      lat?: number;
      lng?: number;
      placeId?: string;
      googleMapsUri?: string;
      mapsUrl?: string;
      /**
       * Dados VISUAIS publicados do cartão (opacos para este módulo). São
       * congelados na publicação: a UI lê-os por `scheduleEntryId` e nunca
       * por índice de arrays de trabalho.
       */
      card?: unknown;
    }
  | {
      kind: "break";
      logicalId?: string;
      day: number;
      /** lunch | dinner | breakfast | hotel | arrival | airport | checkout */
      meal: string;
      label: string;
      startAt: Date;
      endAt: Date;
      venue?: SnapshotVenue;
      /** Dados visuais publicados do bloco (opacos para este módulo). */
      card?: unknown;
    };


export type SnapshotEntry = SnapshotEntryInput & {
  /** Identidade estável da entrada dentro desta revisão. */
  scheduleEntryId: string;
  /** Posição cronológica canónica (0..N-1). */
  order: number;
  /** Numeração visível das paragens (1..N); `null` para blocos. */
  stopNumber: number | null;
};

export type ItinerarySnapshot = RouteSnapshot & {
  city: string;
  entries: SnapshotEntry[];
  /** Estrutura canónica de dias — única fonte da interface. */
  days: RouteDay<SnapshotEntry>[];
  /** Entradas rejeitadas por falta de horas válidas (nunca chegam à UI). */
  droppedEntries: number;
  /** IDs lógicos de entradas obrigatórias sem horas — impedem a publicação. */
  droppedRequired: string[];
  /** Motivo por entrada descartada (trace do funil). */
  droppedTrace: Array<{ id: string; reason: "missing_times" }>;
  /** Assinatura do hotel selecionado nesta revisão ("hotel:none" sem hotel). */
  hotelSignature?: string;
  /**
   * Dados imutáveis de cartão por `scheduleEntryId`. Única fonte visual da
   * agenda: nenhum cartão pode ser resolvido por índice mutável.
   */
  entryDataById: Readonly<Record<string, unknown>>;
};

/** Congela o payload visual publicado (profundidade 1 + arrays). */
function freezeCard(card: unknown): unknown {
  if (card == null || typeof card !== "object") return card;
  for (const value of Object.values(card as Record<string, unknown>)) {
    if (Array.isArray(value)) Object.freeze(value);
  }
  return Object.freeze({ ...(card as Record<string, unknown>) });
};

/** Blocos logísticos não têm local próprio: nunca entram no mapa nem na navegação. */
const LOGISTIC_MEALS = LOGISTICS_MEALS;

function coordsOf(e: SnapshotEntry): { lat: number; lng: number } | null {
  const lat = e.kind === "stop" ? e.lat : e.venue?.lat;
  const lng = e.kind === "stop" ? e.lng : e.venue?.lng;
  return typeof lat === "number" && typeof lng === "number" ? { lat, lng } : null;
}

export function buildItinerarySnapshot(input: {
  routeRevision: number;
  generationRequestId?: string;
  city: string;
  entries: readonly SnapshotEntryInput[];
  travelLegs: readonly TravelLeg[];
  budget: RouteBudget;
  hotelSignature?: string;
  /** Janelas por dia: definem a estrutura canónica de dias publicada. */
  dayWindows?: readonly DayWindow[];
}): ItinerarySnapshot {
  // Nenhuma entrada sem horas válidas chega à rota publicada.
  const publishable = input.entries.filter((e) =>
    isPublishableEntry({ scheduleEntryId: "x", day: e.day, startAt: e.startAt, endAt: e.endAt }),
  );
  const droppedEntries = input.entries.length - publishable.length;
  const dropped = input.entries.filter(
    (e) => !isPublishableEntry({ scheduleEntryId: "x", day: e.day, startAt: e.startAt, endAt: e.endAt }),
  );
  const droppedTrace = dropped.map((e) => ({
    id: e.logicalId ?? (e.kind === "stop" ? e.name : e.label),
    reason: "missing_times" as const,
  }));
  const droppedRequired = dropped
    .filter((e) => e.kind === "stop" && e.required)
    .map((e) => e.logicalId ?? (e.kind === "stop" ? e.name : e.label));
  const ordered = [...publishable].sort((a, b) => a.startAt.getTime() - b.startAt.getTime());
  let stopNumber = 0;
  const seen = new Map<string, number>();
  const entries: SnapshotEntry[] = ordered.map((e, order) => {
    const isStop = e.kind === "stop";
    if (isStop) stopNumber += 1;
    const key = isStop ? (e.placeId ?? e.name) : `${e.meal}:${e.venue?.placeId ?? e.label}`;
    // Identidade ESTÁVEL: quando existe `logicalId` (criado uma única vez na
    // aceitação da paragem/refeição/bloco), é ele o `scheduleEntryId`. Nunca
    // depende do dia, da hora, do índice visual nem da revisão.
    const base = e.logicalId ?? `${e.kind}-${slug(key)}`;
    const dup = seen.get(base) ?? 0;
    seen.set(base, dup + 1);
    // Cópia defensiva: o snapshot publicado nunca pode ser alterado por
    // mutações tardias no rascunho privado (incluindo as datas por referência).
    return Object.freeze({
      ...e,
      startAt: new Date(e.startAt.getTime()),
      endAt: new Date(e.endAt.getTime()),
      order,
      stopNumber: isStop ? stopNumber : null,
      scheduleEntryId: dup === 0 ? base : `${base}-${dup + 1}`,
    }) as SnapshotEntry;
  });
  Object.freeze(entries);

  const base = buildRouteSnapshot({
    routeRevision: input.routeRevision,
    ...(input.generationRequestId ? { generationRequestId: input.generationRequestId } : {}),
    travelLegs: input.travelLegs,
    budget: input.budget,
  });
  const days = buildRouteDays<SnapshotEntry>({
    entries,
    ...(input.dayWindows ? { dayWindows: input.dayWindows } : {}),
  });
  Object.freeze(days);
  const entryDataById: Record<string, unknown> = {};
  for (const e of entries) if (e.card !== undefined) entryDataById[e.scheduleEntryId] = freezeCard(e.card);

  return {
    ...base,
    city: input.city,
    entries,
    days,
    entryDataById: Object.freeze(entryDataById),
    droppedEntries,
    droppedRequired,
    droppedTrace,
    ...(input.hotelSignature ? { hotelSignature: input.hotelSignature } : {}),
  };
}

function slug(s: string) {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 40);
}

export type SnapshotRoutePoint = {
  scheduleEntryId: string;
  order: number;
  name: string;
  lat?: number;
  lng?: number;
  placeId?: string;
  googleMapsUri?: string;
  day: number;
};

/** Pontos do mapa e da navegação — mesma ordem de `scheduleEntryId` da agenda. */
export function snapshotRoutePoints(snapshot: ItinerarySnapshot): SnapshotRoutePoint[] {
  const points: SnapshotRoutePoint[] = [];
  for (const e of snapshot.entries) {
    if (e.kind === "break" && LOGISTIC_MEALS.has(e.meal)) continue;
    const c = coordsOf(e);
    if (e.kind === "break" && !c) continue;
    points.push({
      scheduleEntryId: e.scheduleEntryId,
      order: e.order,
      name: e.kind === "stop" ? e.name : (e.venue?.name ?? e.label),
      ...(c ? { lat: c.lat, lng: c.lng } : {}),
      ...(e.kind === "stop"
        ? { ...(e.placeId ? { placeId: e.placeId } : {}), ...(e.googleMapsUri ? { googleMapsUri: e.googleMapsUri } : {}) }
        : e.venue?.placeId
          ? { placeId: e.venue.placeId }
          : {}),
      day: e.day,
    });
  }
  return points;
}

/** Eventos ICS — mesmos itens, ordem e horários da agenda. */
export function snapshotIcsEvents(snapshot: ItinerarySnapshot): IcsEvent[] {
  return snapshot.entries.map((e) => {
    if (e.kind === "stop") {
      return {
        uid: `${e.scheduleEntryId}@routify`,
        title: `${e.stopNumber}. ${e.name}`,
        ...(e.blurb || e.note
          ? { description: [e.blurb, e.note ? `📝 ${e.note}` : ""].filter(Boolean).join("\n") }
          : {}),
        location: `${e.name}, ${snapshot.city}`,
        start: e.startAt,
        end: e.endAt,
      };
    }
    return {
      uid: `${e.scheduleEntryId}@routify`,
      title: e.venue ? `${e.label} · ${e.venue.name}` : e.label,
      location: e.venue?.name ? `${e.venue.name}, ${snapshot.city}` : snapshot.city,
      start: e.startAt,
      end: e.endAt,
    };
  });
}

export type SnapshotPdfDay = {
  day: number;
  entries: SnapshotEntry[];
  mapPoints: Array<{ lat: number; lng: number }>;
  unmappedNames: string[];
};

/** Dias do PDF — exatamente os `days` canónicos da rota publicada. */
export function snapshotPdfDays(snapshot: ItinerarySnapshot): SnapshotPdfDay[] {
  return snapshot.days
    .map((d) => {
      const entries = [...d.entries].sort((a, b) => a.order - b.order);
      const stops = entries.filter((e) => e.kind === "stop");
      return {
        day: d.dayIndex,
        entries,
        mapPoints: stops
          .map((s) => coordsOf(s))
          .filter((c): c is { lat: number; lng: number } => c != null),
        unmappedNames: stops.filter((s) => coordsOf(s) == null).map((s) => s.name),
      };
    });
}

/**
 * Guarda de revisão: um resultado assíncrono só é aplicado quando pertence à
 * revisão atual ou a uma posterior. Nunca se mistura revisão antiga com nova.
 */
export function acceptSnapshotUpdate(
  current: { routeRevision: number } | null | undefined,
  incoming: { routeRevision: number },
): boolean {
  if (!current) return true;
  return incoming.routeRevision >= current.routeRevision;
}
